import { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { GlassCard, API } from './GlassCard';

export default function Analytics() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/vendors/me/analytics`, { headers: getAuthHeaders() });
        if (res.ok) setData(await res.json());
      } catch (e) {
        console.error('Analytics error:', e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <GlassCard className="p-8 text-center"><p className="text-[#9CA3AF]">Unable to load analytics</p></GlassCard>;

  const formatResponseTime = (mins) => {
    if (!mins) return 'N/A';
    if (mins < 60) return `${Math.round(mins)} min`;
    if (mins < 1440) return `${Math.round(mins / 60)} hrs`;
    return `${Math.round(mins / 1440)} days`;
  };

  const stats = [
    { label: 'Profile Views', value: data.profile_views?.last_30_days || 0, sub: '30 days', icon: 'Views' },
    { label: 'Inquiries', value: data.inquiries?.last_30_days || 0, sub: '30 days', icon: 'Inq' },
    { label: 'Bookings', value: data.bookings?.last_30_days || 0, sub: '30 days', icon: 'Bkg' },
    { label: 'Conversion', value: `${data.conversion_rate || 0}%`, sub: 'inquiry → booking', icon: '%' },
    { label: 'Avg Response', value: formatResponseTime(data.avg_response_minutes), sub: 'to inquiries', icon: 'Rsp' },
    { label: 'Rating', value: data.reviews?.avg_rating?.toFixed(1) || '0.0', sub: `${data.reviews?.count || 0} reviews`, icon: 'Rtg' },
  ];

  return (
    <div className="space-y-6" data-testid="vendor-analytics">
      <div>
        <h2 className="text-2xl font-bold text-white font-syne flex items-center gap-2"><TrendingUp size={22} className="text-[#A78BFA]" /> Analytics</h2>
        <p className="text-[#9CA3AF] text-sm mt-1">Track your profile performance and sales metrics</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {stats.map((s) => (
          <GlassCard key={s.label} className="p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#8B5CF6]/20 text-[#A78BFA]">{s.icon}</span>
              <span className="text-[#64748B] text-xs">{s.label}</span>
            </div>
            <p className="text-white font-mono font-bold text-2xl">{s.value}</p>
            <p className="text-[#64748B] text-[10px] mt-0.5">{s.sub}</p>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-5">
        <h3 className="text-white font-semibold mb-4 text-sm">Profile Views — Last 7 Days</h3>
        <div className="flex items-end gap-2 h-32">
          {(data.profile_views?.daily || []).map((d, i) => {
            const max = Math.max(...(data.profile_views?.daily || []).map((x) => x.views), 1);
            const h = Math.max((d.views / max) * 100, 4);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[#A78BFA] text-[10px] font-mono">{d.views}</span>
                <div className="w-full rounded-t-lg bg-gradient-to-t from-[#8B5CF6] to-[#3B82F6]" style={{ height: `${h}%` }} />
                <span className="text-[#64748B] text-[10px]">{d.day}</span>
              </div>
            );
          })}
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <GlassCard className="p-4 text-center">
          <p className="text-[#64748B] text-[10px] mb-1">Total Views</p>
          <p className="text-white font-mono font-bold text-xl">{data.profile_views?.total || 0}</p>
        </GlassCard>
        <GlassCard className="p-4 text-center">
          <p className="text-[#64748B] text-[10px] mb-1">Total Inquiries</p>
          <p className="text-white font-mono font-bold text-xl">{data.inquiries?.total || 0}</p>
        </GlassCard>
        <GlassCard className="p-4 text-center">
          <p className="text-[#64748B] text-[10px] mb-1">Completed Jobs</p>
          <p className="text-white font-mono font-bold text-xl">{data.bookings?.completed || 0}</p>
        </GlassCard>
        <GlassCard className="p-4 text-center">
          <p className="text-[#64748B] text-[10px] mb-1">Total Revenue</p>
          <p className="text-green-400 font-mono font-bold text-xl">${data.revenue?.total?.toFixed(0) || '0'}</p>
        </GlassCard>
      </div>
    </div>
  );
}
